import { routesByPathname } from 'enums/routes'
import { InstructorAuthContextProps } from 'shared/providers/AuthProvider'
import findParentRouteByPathname from './findRouteByPathname'

export enum AccessStatus {
  authNotRequired = 'auth not required',
  loading = 'auth loading',
  authenticated = 'authenticated',
  unauthenticated = 'unauthenticated',
  authorised = 'authorised',
  unauthorised = 'unauthorised'
}

export enum AccessAction {
  allowAccess = 'allow access',
  redirectToLogin = 'redirect to login',
  indicateUnauthorised = 'indicate unauthorised',
  indicateLoading = 'indicate loading'
}

/**
 * determineAccess used for determining access for a user on a particular page based on their assigned outlets
 *
 * Note this is separate to permissions - which is more specific and looks at the roles for specific users to determine
 * changes that can be made at a feature level
 */
export const determineAccess = ({
  pathname,
  instructor,
  outletSlug,
  authLoading,
  authenticated
}: {
  pathname: string
  instructor: InstructorAuthContextProps['instructor']
  outletSlug: string
  authLoading: InstructorAuthContextProps['authLoading']
  authenticated: InstructorAuthContextProps['authenticated']
}) => {
  const authRequired = determineAuthRequired(pathname)
  const isAuthorised = isAuthorisedToAccessPage(instructor, outletSlug)

  if (!authRequired) {
    return {
      status: [AccessStatus.authNotRequired],
      action: AccessAction.allowAccess
    }
  }

  if (authLoading) {
    return {
      status: [AccessStatus.loading],
      action: AccessAction.indicateLoading
    }
  }

  if (authenticated && isAuthorised) {
    return {
      status: [AccessStatus.authenticated, AccessStatus.authorised],
      action: AccessAction.allowAccess
    }
  }

  if (authenticated && !isAuthorised) {
    return {
      status: [AccessStatus.authenticated, AccessStatus.unauthorised],
      action: AccessAction.indicateUnauthorised
    }
  }

  if (!authenticated) {
    return {
      status: [AccessStatus.unauthenticated],
      action: AccessAction.redirectToLogin
    }
  }
}

export default determineAccess

export const isAuthorisedToAccessPage = (
  instructor: InstructorAuthContextProps['instructor'],
  outletSlug: string
) => {
  if (!instructor) {
    return false
  }
  if (!outletSlug) {
    return true
  }

  const associatedOutlet = instructor.instructors_outlets?.find(
    ({ outlet }) => outlet?.slug === outletSlug
  )

  return !!associatedOutlet
}

export const determineAuthRequired = (pathname: string) => {
  if (routesByPathname?.[pathname]) {
    return routesByPathname[pathname]?.authRequired
  }

  const parentRouteByPathname = findParentRouteByPathname(pathname)
  if (parentRouteByPathname) {
    return !!parentRouteByPathname.authRequired
  }

  /** If we are here it means the pathname is not found nor a parent is found in routes
   * This means that for whatever reason the route does not exist in routes.ts yet.
   * Return true as a default - almost all pages in Nebula you will need to be authenticated
   */
  return true
}
